import React from "react";
import { Box, Radio, Typography } from "@mui/material";
import { type TabData } from "@utils/dashboard-storage";

interface DestinationTabListProps {
  tabs: TabData[];
  selectedTabId: string | null;
  onSelect: (tabId: string) => void;
}

interface TabRowGroup {
  groupName: string | null;
  tabs: TabData[];
}

const SelectableTabRow: React.FC<{
  tab: TabData;
  isSelected: boolean;
  onSelect: (tabId: string) => void;
}> = ({ tab, isSelected, onSelect }) => (
  <Box
    onClick={() => onSelect(tab.id)}
    sx={{
      display: "flex",
      alignItems: "center",
      width: "100%",
      mb: "10px",
      pl: 1,
      pr: 2,
      borderRadius: "8px",
      border: isSelected ? "2px solid #1976d2" : "1px solid #E5E7EB",
      bgcolor: isSelected ? "#EFF6FF" : "white",
      cursor: "pointer",
      "&:hover": { borderColor: "#1976d2" },
    }}
  >
    <Radio
      checked={isSelected}
      size="small"
      onChange={() => onSelect(tab.id)}
    />
    <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 400 }}>
      {tab.label}
    </Typography>
  </Box>
);

/** Same grouped visual language as the Edit Tabs modal (GroupContainer/TabRow):
 *  grouped tabs sit inside a bordered, rounded container under their group
 *  name; ungrouped tabs render as plain rows. Read-only — no drag/rename/
 *  delete affordances, single-select instead of editing. */
const DestinationTabList: React.FC<DestinationTabListProps> = ({
  tabs,
  selectedTabId,
  onSelect,
}) => {
  const groups: TabRowGroup[] = [];
  tabs.forEach((tab) => {
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.groupName === tab.groupName) {
      lastGroup.tabs.push(tab);
    } else {
      groups.push({ groupName: tab.groupName, tabs: [tab] });
    }
  });

  if (tabs.length === 0) {
    return (
      <Typography variant="body2" className="text-gray-500 px-2 py-3">
        No other tabs available.
      </Typography>
    );
  }

  return (
    <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
      {groups.map((group, groupIndex) =>
        group.groupName ? (
          <Box
            key={`${group.groupName}-${groupIndex}`}
            sx={{
              border: "1px solid black",
              borderRadius: "8px",
              mb: "10px",
              pb: "10px",
            }}
          >
            <Typography
              sx={{ px: 2, pt: "10px", pb: "8px", fontWeight: 600, fontSize: "14px" }}
            >
              {group.groupName}
            </Typography>
            <Box sx={{ px: 2 }}>
              {group.tabs.map((tab) => (
                <SelectableTabRow
                  key={tab.id}
                  tab={tab}
                  isSelected={selectedTabId === tab.id}
                  onSelect={onSelect}
                />
              ))}
            </Box>
          </Box>
        ) : (
          <Box key={`ungrouped-${groupIndex}`}>
            {group.tabs.map((tab) => (
              <SelectableTabRow
                key={tab.id}
                tab={tab}
                isSelected={selectedTabId === tab.id}
                onSelect={onSelect}
              />
            ))}
          </Box>
        ),
      )}
    </Box>
  );
};

export default DestinationTabList;
